/**
 * @documental
 * @export Button
 * @import
 * import { Button } from "capitalrx-components-2.0/lib/general";
 */
import { AriaAttributes, Children, ForwardedRef, KeyboardEvent, MouseEvent, ReactNode, RefCallback, forwardRef, useCallback, useRef } from "react";

import { BaseProps } from "capitalrx-components-2.0/lib/BaseProps";
import { RefComponent } from "capitalrx-components-2.0/lib/forms";
import { DarkGrey, Transparent } from "capitalrx-components-2.0/lib/general/Color";
import { useDisabled } from "capitalrx-components-2.0/lib/general/DisableGroup/useDisabled";
import { Icon, IconName } from "capitalrx-components-2.0/lib/general/Icon";
import { Tooltip } from "capitalrx-components-2.0/lib/general/Tooltip";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Bold, Regular, Semibold } from "capitalrx-components-2.0/lib/typography";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib/utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "capitalrx-components-2.0/lib/utilities/withSubComponents";

import { ButtonAppearance } from "./ButtonAppearances";
import { ButtonClickEventHandler } from "./ButtonClickEventHandler";
import { ButtonSize } from "./ButtonSize";
import { Placeholder } from "./Placeholder";
import { StyledButton } from "./StyledButton";
import { getAppearanceForString } from "./helpers";

import styles from "./styles.module.scss";
import { KeyPressEventHandler } from "./KeyPressEventHandler";

/**
 * The `Button` component properties.
 */
export interface ButtonProps extends BaseProps {
  /**
   * The button appearance. If none specified defaults to `"primary"`.
   */
  readonly appearance?: "primary" | "secondary" | "text" | "destructive" | "positive" | ButtonAppearance;

  /**
   * Indicates whether the button background should be transparent.
   * @deprecated This should be done through the use of a specific appearance.
   */
  readonly transparent?: boolean;

  /**
   * The button size. If none specified defaults to `"large"`.
   * @deprecated This should be done through the use of a specific appearance.
   */
  readonly size?: ButtonSize;

  /**
   * The button icon. See the [Icon](../Icon/README.md) component for a
   * list of available icons and their names.
   */
  readonly icon?: IconName;

  /**
   * The icon position. If not specified defaults to `"left"`.
   */
  readonly iconPosition?: "left" | "right";

  /**
   * Indicates whether the button is disabled. If not specified defaults to `false`.
   */
  readonly disabled?: boolean;

  /**
   * Function to call when the button is clicked.
   */
  readonly onClick?: ButtonClickEventHandler;

  /**
   * Function to call when the keyboard is pressed.
   */
  readonly onKeyPress?: KeyPressEventHandler;

  /**
   * The button tab index.
   */
  readonly tabIndex?: number;

  /**
   * The button tooltip.
   */
  readonly tooltip?: ReactNode;

  /**
   * The button children.
   */
  readonly children?: ReactNode;

  /**
   * Automatically focuses the button. If not specified defaults to `false`.
   */
  readonly autoFocus?: boolean;

  /**
   * The ARIA label for the button, used for accessibility.
   */
  readonly "aria-label"?: string;
  /**
   * The ARIA busy for the button, indicates whether the button is currently busy.
   */
  readonly "aria-busy"?: boolean;

  /**
   * The id or space-separated list of element ids that describe the current element.
   */
  readonly "aria-describedby"?: AriaAttributes["aria-describedby"];

  /** Indicates the trigger opens a menu. */
  readonly "aria-haspopup"?: AriaAttributes["aria-haspopup"];

  /** Expanded state for composite widgets like menus. */
  readonly "aria-expanded"?: AriaAttributes["aria-expanded"];

  /** Id of the element controlled by this button. */
  readonly "aria-controls"?: AriaAttributes["aria-controls"];
}

/**
 * The `Button` component sub components.
 */
export interface ButtonSubComponents {
  /**
   * A placeholder component for the Button which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

export const CloseButtonAppearance: ButtonAppearance = {
  padding: { horizontal: 0, vertical: 0 },
  label: {
    letterSpacing: 0,
    uppercase: false,
    weight: "regular",
    fontSize: 16,
    lineHeight: 16,
  },
  icon: {
    size: "regular",
  },
  normal: {
    backgroundColor: Transparent,
    foregroundColor: DarkGrey[500],
    borderColor: Transparent,
  },
  hover: {
    backgroundColor: Transparent,
    foregroundColor: DarkGrey[400],
    borderColor: Transparent,
  },
  pressed: {
    backgroundColor: Transparent,
    foregroundColor: DarkGrey[600],
    borderColor: Transparent,
  },
  disabled: {
    backgroundColor: Transparent,
    foregroundColor: DarkGrey[400],
    borderColor: Transparent,
  },
};

/**
 * A regular button.
 */
export const Button = withSubComponents<RefComponent<HTMLButtonElement, ButtonProps>, ButtonSubComponents>(
  {
    Placeholder,
  },
  forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, outerRef) {
    const {
      appearance = "primary",
      size = "large",
      icon,
      iconPosition = "left",
      children,
      tooltip,
      transparent = false,
      tabIndex = 0,
      autoFocus = false,
      onClick,
      onKeyPress,
      "aria-label": ariaLabel,
      "aria-busy": ariaBusy,
      "aria-describedby": ariaDescribedby,
      "aria-haspopup": ariaHaspopup,
      "aria-expanded": ariaExpanded,
      "aria-controls": ariaControls,
    } = props;

    const disabled = useDisabled(props);

    const appearanceData = typeof appearance === "string" ? getAppearanceForString(appearance, transparent, size) : appearance;

    const nonEmptyChildren = Children.toArray(children).filter((child) => {
      if (child === null || child === undefined || child === "") return false;
      return true;
    });

    const iconOnly = !!(icon && nonEmptyChildren.length === 0);

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [
        styles.Button,
        "button",
        appearanceData.className,
        size,
        {
          "icon-only": iconOnly,
          transparent,
          disabled,
        },
      ],
      testIdPrefix: "button",
    });

    const clickHandler = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        if (disabled) return;
        onClick?.(e);
      },
      [disabled, onClick],
    );

    const keyPressHandler = useCallback(
      (e: KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;
        onKeyPress?.(e);
      },
      [disabled, onKeyPress],
    );

    const previousOuterRef = useRef<ForwardedRef<HTMLButtonElement> | null>(null);
    const previousInnerRef = useRef<RefCallback<HTMLButtonElement> | null>(null);
    const previousRefHandler = useRef<RefCallback<HTMLButtonElement> | null>(null);

    const refHandler = useCallback(
      (innerRef?: RefCallback<HTMLButtonElement>) => {
        if (previousInnerRef.current === innerRef && previousOuterRef.current === outerRef && previousRefHandler.current !== null) {
          return previousRefHandler.current;
        }

        previousInnerRef.current = innerRef ?? null;
        previousOuterRef.current = outerRef ?? null;

        return (previousRefHandler.current = (ref: HTMLButtonElement | null) => {
          if (innerRef) innerRef(ref);
          if (outerRef) {
            if (typeof outerRef === "function") outerRef(ref);
            else outerRef.current = ref;
          }
        });
      },
      [outerRef],
    );

    let LabelContainer = Regular;
    if (appearanceData.label.weight === "bold") LabelContainer = Bold;
    else if (appearanceData.label.weight === "semibold") LabelContainer = Semibold;

    const content = (ref?: RefCallback<HTMLButtonElement>) => (
      <StyledButton
        {...baseHtmlProps}
        appearance={appearanceData}
        iconOnly={iconOnly}
        tabIndex={tabIndex}
        ref={refHandler(ref)}
        disabled={disabled}
        autoFocus={autoFocus}
        type="button"
        onClick={clickHandler}
        onKeyDown={keyPressHandler}
        aria-label={ariaLabel}
        aria-busy={ariaBusy}
        aria-describedby={ariaDescribedby}
        aria-haspopup={ariaHaspopup}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
      >
        <Shelf spacing="small">
          {icon && (iconPosition === "left" || iconOnly) && (
            <Icon className={styles.Icon} name={icon} size={appearanceData?.icon?.size ?? "regular"} aria-label={`${ariaLabel} icon`}/>
          )}
          {!iconOnly && <LabelContainer>{children ?? <>&nbsp;</>}</LabelContainer>}
          {icon && iconPosition === "right" && !iconOnly && (
            <Icon className={styles.Icon} name={icon} size={appearanceData?.icon?.size ?? "regular"} aria-label={`${ariaLabel} icon`}/>
          )}
        </Shelf>
      </StyledButton>
    );

    return (
      <>
        {tooltip && <Tooltip content={tooltip}>{content}</Tooltip>}
        {!tooltip && content()}
      </>
    );
  }),
);
